import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { api, formatApiErrorDetail, IMG } from "@/lib/api";

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 2.09 14.96 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.72 7.04 9.16 5.04 12 5.04z"/>
      <path fill="#4285F4" d="M23 12.25c0-.79-.07-1.54-.19-2.25H12v4.51h6.17c-.27 1.43-1.07 2.64-2.29 3.46l3.59 2.78C21.56 18.9 23 15.86 23 12.25z"/>
      <path fill="#FBBC05" d="M5.84 14.09a7.1 7.1 0 0 1 0-4.18L2.18 7.07a11 11 0 0 0 0 9.86l3.66-2.84z"/>
      <path fill="#34A853" d="M12 23c2.96 0 5.46-.98 7.28-2.65l-3.59-2.78c-1 .67-2.27 1.07-3.69 1.07-2.84 0-5.28-2-6.16-4.87l-3.66 2.84C3.99 20.53 7.7 23 12 23z"/>
    </svg>
  );
}

export default function Login() {
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/members";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      await api.post(mode === "register" ? "/auth/register" : "/auth/login", payload);
      navigate("/members");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-32" data-testid="login-page">
      <div className="absolute inset-0">
        <img src={IMG.celestialSky} alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/60 to-ink" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-line relative z-10 w-full max-w-md p-10"
        data-testid="login-card"
      >
        <Logo size={48} />
        <h1 className="mt-8 font-display text-3xl font-semibold text-silver">
          {mode === "signin" ? "Welcome back" : "Join the circle"}
        </h1>
        <p className="mt-2 text-xs text-silver-muted">
          {mode === "signin" ? "Sign in to your RCSCW member account." : "Create your RCSCW member account."}
        </p>

        <button onClick={googleSignIn} className="btn-silver mt-8 w-full justify-center" data-testid="google-signin-btn">
          <GoogleIcon />
          <span>Continue with Google</span>
        </button>

        <div className="my-7 flex items-center gap-4">
          <span className="h-px flex-1 bg-silver/15" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-silver-muted">or with email</span>
          <span className="h-px flex-1 bg-silver/15" />
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="member-login-form">
          {mode === "register" && (
            <input className="input-field" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" />
          )}
          <input type="email" required className="input-field" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" />
          <input type="password" required className="input-field" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-password-input" />
          <button type="submit" disabled={busy} className="btn-ember w-full justify-center disabled:opacity-60" data-testid="member-submit-btn">
            <span>{busy ? "One moment…" : mode === "signin" ? "Sign In" : "Create Account"}</span>
            {busy && <Loader2 size={14} className="animate-spin" />}
          </button>
        </form>

        <button onClick={() => setMode(mode === "signin" ? "register" : "signin")} className="mt-6 w-full text-center text-xs text-silver-muted underline-offset-4 hover:text-white hover:underline" data-testid="login-mode-toggle">
          {mode === "signin" ? "New here? Create a member account" : "Already have an account? Sign in"}
        </button>

        <Link to="/" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted hover:text-white" data-testid="login-back-home-link">
          <ArrowLeft size={12} /> Back to the website
        </Link>
      </motion.div>
      <Toaster position="bottom-right" theme="dark" />
    </main>
  );
}
